import React, { useState } from "react";
import { toast } from "sonner";
import { Minus, Plus, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";

const BookingDialog = ({ event, open, onOpenChange }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [participants, setParticipants] = useState(1);
  const [loading, setLoading] = useState(false);

  const total = (Number(event?.price || 0) * participants).toFixed(2);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/bookings/checkout", {
        event_id: event.id,
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
        participants,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-dialog"
        className="bg-[#0d0d0d] border border-white/10 text-white sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Prenota — {event?.title}</DialogTitle>
          <DialogDescription className="text-white/50">
            Compila i dati e procedi al pagamento sicuro online.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <input
            required
            data-testid="booking-name"
            placeholder="Nome e cognome"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bg-[#121212] border border-white/10 rounded-lg px-4 py-3 placeholder:text-white/40 focus:border-[#FFF200] focus:outline-none transition-colors"
          />
          <input
            required
            type="email"
            data-testid="booking-email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full bg-[#121212] border border-white/10 rounded-lg px-4 py-3 placeholder:text-white/40 focus:border-[#FFF200] focus:outline-none transition-colors"
          />
          <input
            required
            data-testid="booking-phone"
            placeholder="Telefono"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="w-full bg-[#121212] border border-white/10 rounded-lg px-4 py-3 placeholder:text-white/40 focus:border-[#FFF200] focus:outline-none transition-colors"
          />

          <div className="flex items-center justify-between bg-[#121212] border border-white/10 rounded-lg px-4 py-3">
            <span className="text-white/70 text-sm">Numero di persone</span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                data-testid="booking-decrement"
                onClick={() => setParticipants((p) => Math.max(1, p - 1))}
                className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
              >
                <Minus size={14} />
              </button>
              <span data-testid="booking-participants" className="w-6 text-center font-semibold">
                {participants}
              </span>
              <button
                type="button"
                data-testid="booking-increment"
                onClick={() => setParticipants((p) => Math.min(54, p + 1))}
                className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
              >
                <Plus size={14} />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            <span className="text-white/60">Totale</span>
            <span data-testid="booking-total" className="text-2xl font-display font-bold text-[#FFF200]">
              €{total}
            </span>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="booking-pay-btn"
            className="w-full flex items-center justify-center gap-2 bg-[#FFF200] text-black font-semibold py-3.5 rounded-full hover:bg-[#FFF200]/90 transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : null}
            {loading ? "Reindirizzamento..." : "Paga e prenota"}
          </button>
          <p className="text-center text-white/30 text-xs">Pagamento sicuro tramite Stripe</p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BookingDialog;
